import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import { KTCard, KTCardBody } from '../../../../_metronic/helpers'
import { KTIcon } from '../../../../_metronic/helpers'
import { EditTag } from './EditTag'
import { 
  get_all_tags,
  delete_tag,
  update_tag
} from '../api/tagsApi'
import { toast } from 'react-toastify'
import { LoadingProgressBar } from '../../projects/components/LoadingProgressBar'
import { permissionCheck } from '../../../helper-functions/PermissionCheck'

// Tag type for this module
interface TagItem {
  id: number
  tag_title: string
  description: string
  created_at: string
  updated_at: string
  status: number
}

const TagsList: React.FC = () => {
  const navigate = useNavigate()
  const [tags, setTags] = useState<TagItem[]>([])
  const [filteredTags, setFilteredTags] = useState<TagItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedTag, setSelectedTag] = useState<TagItem | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [tagToDelete, setTagToDelete] = useState<TagItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const canViewTags = Boolean(permissionCheck('tag-view'))
  const canCreateTag = Boolean(permissionCheck('tag-create'))
  const canUpdateTag = Boolean(permissionCheck('tag-update'))
  const canDeleteTag = Boolean(permissionCheck('tag-delete'))

  // Load tags
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        if (canViewTags) {
          await fetchAllTags()
        }
      } catch (error) {
        console.error('Error loading tags:', error)
        toast.error('Failed to load tags')
      } finally {
        setIsLoading(false)
      }
    }
    if (canViewTags) {
      loadData()
    }
  }, [canViewTags])

  const fetchAllTags = async () => {
    try {
      const res = await get_all_tags()
      const apiTags = res?.data?.all_tags || res?.data?.tags || res?.data || []
      const all: TagItem[] = (Array.isArray(apiTags) ? apiTags : []).map((t: any) => ({
        id: Number(t.id),
        tag_title: t.tag_title,
        description: t.description || '',
        created_at: t.created_at,
        updated_at: t.updated_at,
        status: Number(t.status)
      }))
      setTags(all)
      setFilteredTags(all)
    } catch (error) {
      console.error('Error fetching tags:', error)
      toast.error('Failed to load tags')
      setTags([])
      setFilteredTags([])
    }
  }

  // Filter tags based on search term and status
  useEffect(() => {
    let filtered = tags

    if (searchTerm) {
      filtered = filtered.filter(tag =>
        tag.tag_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tag.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedStatus !== '') {
      filtered = filtered.filter(tag => String(tag.status) === selectedStatus)
    }

    setFilteredTags(filtered)
  }, [tags, searchTerm, selectedStatus])

  // Pagination derived values
  const totalPages = Math.max(1, Math.ceil(filteredTags.length / pageSize))
  const pageSafe = Math.min(currentPage, totalPages)
  const startIdx = (pageSafe - 1) * pageSize
  const endIdx = startIdx + pageSize
  const paginatedTags = filteredTags.slice(startIdx, endIdx)

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedStatus, pageSize])


  const handleEditTag = async (updatedTag: TagItem) => {
    if (!canUpdateTag) {
      toast.error('You do not have permission to update tags')
      return
    }
    try {
      const payload = {
        tag_title: updatedTag.tag_title,
        description: updatedTag.description,
        status: String(updatedTag.status)
      }
      await update_tag(String(updatedTag.id), payload)
      toast.success('Tag updated')
      // Refresh all tags and rely on filters
      await fetchAllTags()
      setShowEditModal(false)
      setSelectedTag(null)
    } catch (error: any) {
      console.error('Error updating tag:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update tag'
      toast.error(errorMessage)
    }
  }

  const handleDeleteClick = (tag: TagItem) => {
    if (!canDeleteTag) {
      toast.error('You do not have permission to delete tags')
      return
    }
    setTagToDelete(tag)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!canDeleteTag) {
      toast.error('You do not have permission to delete tags')
      return
    }
    if (!tagToDelete) return
    try {
      await delete_tag(String(tagToDelete.id))
      toast.success('Tag deleted')
      await fetchAllTags()
    } catch (error: any) {
      console.error('Error deleting tag:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete tag'
      toast.error(errorMessage)
    } finally {
      setShowDeleteModal(false)
      setTagToDelete(null)
    }
  }

  const handleEditClick = (tag: TagItem) => {
    if (!canUpdateTag) {
      toast.error('You do not have permission to update tags')
      return
    }
    setSelectedTag(tag)
    setShowEditModal(true)
  }

  if (!canViewTags) {
    return (
      <KTCard>
        <KTCardBody>
          <div className='alert alert-warning mb-0'>
            You do not have permission to view tags.
          </div>
        </KTCardBody>
      </KTCard>
    )
  }

  return (
    <>
      <LoadingProgressBar isLoading={isLoading} />
      <KTCard>
        <KTCardBody>
          <div className='d-flex flex-column flex-lg-row'>
            <div className='flex-lg-row-fluid mb-10 mb-lg-0 me-lg-7'>
              <h3 className='fw-bold text-dark'>Tags Management</h3>
              <p className='text-muted fs-6'>Manage your tags</p>
            </div>
            {canCreateTag && (
              <div className='d-flex align-items-center'>
                <button
                  className='btn btn-primary'
                  onClick={() => navigate('/tags/add')}
                >
                  <KTIcon iconName='plus' className='fs-2' />
                  Add Tag
                </button>
              </div>
            )}
          </div>
        </KTCardBody>
      </KTCard>

      <KTCard>
        <KTCardBody>
          {/* Search and Filter Section */}
          <div className='d-flex align-items-center gap-3 flex-wrap mb-6'>
            <div className='position-relative' style={{minWidth: 280}}>
              <KTIcon
                iconName='magnifier'
                className='fs-3 position-absolute top-50 translate-middle-y ms-3'
              />
              <input
                type='text'
                className='form-control form-control-solid ps-13'
                placeholder='Search tags...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{minWidth: 280}}
              />
            </div>
            <div style={{minWidth: 160}}>
              <select
                className='form-select form-select-solid'
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value=''>All Status</option>
                <option value='1'>Active</option>
                <option value='0'>Inactive</option>
              </select>
            </div>
            <div className='ms-auto'>
              <button
                className='btn btn-light-primary'
                onClick={() => {
                  setSearchTerm('')
                  setSelectedStatus('')
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Tags Table */}
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='min-w-150px'>Title</th>
                  <th className='min-w-200px'>Description</th>
                  <th className='min-w-80px'>Status</th>
                  <th className='min-w-100px'>Updated At</th>
                  <th className='min-w-100px text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({length: 6}).map((_, idx) => (
                    <tr key={`sk-${idx}`}>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-8'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-10'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-6'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-4'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-5'></span>
                        </div>
                      </td>
                      <td className='text-end'>
                        <div className='d-flex justify-content-end flex-shrink-0 placeholder-glow'>
                          <span className='placeholder btn btn-icon btn-bg-light btn-sm me-1 col-2'></span>
                          <span className='placeholder btn btn-icon btn-bg-light btn-sm col-2'></span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : filteredTags.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='text-center py-10'>
                      <div className='text-muted'>No tags found</div>
                    </td>
                  </tr>
                ) : (
                  paginatedTags.map((tag) => (
                    <tr key={tag.id}>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <span className='text-dark fw-bold text-hover-primary fs-6'>
                              {tag.tag_title}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span 
                          className='text-muted fw-bold text-muted d-block fs-7'
                          style={{
                            maxWidth: '250px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={tag.description || 'N/A'}
                        >
                          {tag.description || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${tag.status === 1 ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
                          {tag.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <span className='text-muted fw-bold text-muted d-block fs-7'>
                          {tag.updated_at ? new Date(tag.updated_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td className='text-end'>
                        <div className='d-flex justify-content-end flex-shrink-0'>
                          {canUpdateTag && (
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                              onClick={() => handleEditClick(tag)}
                            >
                              <KTIcon iconName='pencil' className='fs-3' />
                            </button>
                          )}
                          {canDeleteTag && (
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                              onClick={() => handleDeleteClick(tag)}
                            >
                              <KTIcon iconName='trash' className='fs-3' />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div className='d-flex align-items-center justify-content-between mt-4'>
            <div className='d-flex align-items-center gap-3'>
              <span className='text-muted fs-7'>
                Showing {filteredTags.length === 0 ? 0 : startIdx + 1}-{Math.min(endIdx, filteredTags.length)} of {filteredTags.length}
              </span>
              <div className='d-flex align-items-center'>
                <span className='me-2 text-muted fs-7'>Show</span>
                <select
                  className='form-select form-select-sm w-auto'
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className='ms-2 text-muted fs-7'>per page</span>
              </div>
            </div>
            <ul className='pagination'>
              <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(1)}>«</button>
              </li>
              <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Prev</button>
              </li>
              <li className='page-item active'>
                <span className='page-link'>{pageSafe} / {totalPages}</span>
              </li>
              <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
              </li>
              <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(totalPages)}>»</button>
              </li>
            </ul>
          </div>
        </KTCardBody>
      </KTCard>


      {/* Edit Tag Modal */}
      {showEditModal && selectedTag && (
        <EditTag
          tag={selectedTag}
          onClose={() => {
            setShowEditModal(false)
            setSelectedTag(null)
          }}
          onSave={handleEditTag}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        className='modal fade'
        id='kt_modal_delete_tag'
        tabIndex={-1}
        role='dialog'
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false)
          setTagToDelete(null)
        }}
      >
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content'>
            <div className='modal-header pb-0 border-0 justify-content-end'>
              <div
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={() => {
                  setShowDeleteModal(false)
                  setTagToDelete(null)
                }}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>
            <div className='modal-body scroll-y mx-5 mx-xl-18 pt-0 pb-15'>
              <div className='mb-13 text-center'>
                <KTIcon iconName='question' className='fs-5tx text-danger mb-5' />
                <h1 className='mb-3'>Delete Tag</h1>
                <div className='text-muted fw-semibold fs-5'>
                  Are you sure you want to delete this tag?
                  {tagToDelete && (
                    <div className='mt-3'>
                      <strong className='text-dark'>"{tagToDelete.tag_title}"</strong>
                    </div>
                  )}
                  <br />This action cannot be undone.
                </div>
              </div>
              <div className='d-flex flex-stack'>
                <button
                  className='btn btn-primary'
                  onClick={() => {
                    setShowDeleteModal(false)
                    setTagToDelete(null)
                  }}
                >
                  Cancel
                </button>
                <button className='btn btn-danger' onClick={handleDeleteConfirm}>
                  Delete Tag
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export { TagsList }

